import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Loader2, RefreshCw } from 'lucide-react';
import { Type1BotCard, Type2BotCard } from './BotStateCard';
import { BotWinRateDetailDialog } from './BotWinRateDetailDialog';
import { BulkDeleteType1Dialog } from './BulkDeleteType1Dialog';
import { GroupActivityMonitor } from './GroupActivityMonitor';
import { fetchAllBotsWinRateStats } from '@/services/conditional-skip-stats-service';

interface BotState {
  id: string;
  bot_id: string;
  current_loss_streak: number;
  mode: 'GATE' | 'OPEN';
  opened_at: string | null;
  last_exit_at: string | null;
  last_exit_win: number | null;
  locked_by_max_loss: boolean;
  updated_at: string;
  recent_results?: string;
  active_lock_pattern_id?: string | null;
  hasReversePatterns?: boolean;
  bots?: {
    name: string;
    type: string;
  };
  conditional_skip_settings?: {
    skip_type: 'START_STOP_AFTER_LOSS' | 'FLEXIBLE_PATTERN';
  };
}

export const ConditionalSkipMonitor: React.FC = () => {
  // Sort state for each column
  const [type1SortBy, setType1SortBy] = useState<'name' | 'updated' | 'status' | 'winrate'>('updated');
  const [type2SortBy, setType2SortBy] = useState<'name' | 'updated' | 'status' | 'winrate'>('updated');
  
  // Detail dialog state
  const [selectedBotId, setSelectedBotId] = useState<string | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  // Fetch win rate stats
  const { data: winRateStats, refetch: refetchWinRates } = useQuery({
    queryKey: ['conditional-skip-win-rates'],
    queryFn: async () => {
      return await fetchAllBotsWinRateStats();
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Fetch bot states
  const { 
    data: states = [], 
    isLoading: statesLoading,
    refetch: refetchStates 
  } = useQuery({
    queryKey: ['conditional-skip-states-monitor'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('bot_current_condition_skip')
        .select(`
          *,
          bots:bot_id (
            name,
            type
          )
        `)
        .order('updated_at', { ascending: false });
      
      if (error) throw error;
      
      // Fetch skip_type and check for REVERSE patterns for each bot
      const statesWithSkipType = await Promise.all(
        (data || []).map(async (state) => {
          const { data: settings } = await supabase
            .from('conditional_skip_settings')
            .select('id, skip_type')
            .eq('bot_id', state.bot_id)
            .single();
          
          // Check if bot has any REVERSE patterns configured
          let hasReversePatterns = false;
          if (settings && settings.skip_type === 'FLEXIBLE_PATTERN') {
            const { data: patterns } = await supabase
              .from('flexible_skip_patterns')
              .select('pattern_mode')
              .eq('setting_id', settings.id)
              .eq('pattern_mode', 'REVERSE')
              .limit(1);
            
            hasReversePatterns = patterns && patterns.length > 0;
          }
          
          return {
            ...state,
            conditional_skip_settings: settings ? { skip_type: settings.skip_type } : undefined,
            hasReversePatterns
          };
        })
      );
      
      return statesWithSkipType as BotState[];
    },
    refetchInterval: 5000, // Refresh every 5 seconds
  });


  // Real-time subscription
  useEffect(() => {
    const channel = supabase
      .channel('conditional-skip-monitor-realtime')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'bot_current_condition_skip'
        },
        () => {
          refetchStates();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [refetchStates]);

  // Fetch bot group memberships
  const { data: botGroups } = useQuery({
    queryKey: ['bot-group-memberships'],
    queryFn: async () => {
      // @ts-ignore - strategy_group_members table will be created by migration
      const { data } = await supabase
        // @ts-ignore
        .from('strategy_group_members')
        .select(`
          bot_id,
          priority,
          strategy_groups(name, group_type, is_active)
        `)
        .eq('is_active', true);
      return data as any[];
    },
    refetchInterval: 5000,
  });

  // Separate by skip type
  const type1Bots = states.filter(s => s.conditional_skip_settings?.skip_type === 'START_STOP_AFTER_LOSS');
  const type2Bots = states.filter(s => s.conditional_skip_settings?.skip_type === 'FLEXIBLE_PATTERN');

  // Fetch active lock patterns for Type 2 bots
  const { data: type2LockPatterns } = useQuery({
    queryKey: ['type2-active-lock-patterns', type2Bots.map(b => b.active_lock_pattern_id)],
    queryFn: async () => {
      const patternIds = type2Bots
        .map(b => b.active_lock_pattern_id)
        .filter(id => id !== null && id !== undefined);
      
      if (patternIds.length === 0) return new Map();
      
      const { data } = await supabase
        .from('flexible_skip_patterns')
        .select('id, is_permanent, lock_pattern, pattern_mode')
        .in('id', patternIds);
      
      return new Map(data?.map(p => [p.id, p]) || []);
    },
    enabled: type2Bots.length > 0,
    refetchInterval: 5000,
  });

  // Sorting functions
  const sortByName = (a: BotState, b: BotState) => {
    const nameA = a.bots?.name || '';
    const nameB = b.bots?.name || '';
    return nameA.localeCompare(nameB);
  };

  const sortByUpdated = (a: BotState, b: BotState) => {
    return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
  };

  const sortByStatus = (a: BotState, b: BotState) => {
    // Priority: OPEN (1) -> LOCKED (2) -> GATE (3)
    const getStatusPriority = (state: BotState) => {
      if (state.mode === 'OPEN') return 1;
      if (state.locked_by_max_loss) return 2;
      if (state.mode === 'GATE') return 3;
      return 4;
    };
    return getStatusPriority(a) - getStatusPriority(b);
  };

  const sortByStatusType2 = (a: BotState, b: BotState) => {
    // Priority: OPEN (1) -> GATE (2)
    return a.mode === 'OPEN' ? (b.mode === 'OPEN' ? 0 : -1) : (b.mode === 'OPEN' ? 1 : 0);
  };

  const sortByWinRate = (a: BotState, b: BotState) => {
    const winRateA = winRateStats?.get(a.bot_id)?.win_rate ?? -1;
    const winRateB = winRateStats?.get(b.bot_id)?.win_rate ?? -1;
    
    // Bots with no data go to bottom (-1)
    // Sort descending (highest win rate first)
    return winRateB - winRateA;
  };

  const applySorting = (bots: BotState[], sortBy: string, isType2 = false) => {
    const sorted = [...bots];
    switch(sortBy) {
      case 'name':
        return sorted.sort(sortByName);
      case 'updated':
        return sorted.sort(sortByUpdated);
      case 'status':
        return sorted.sort(isType2 ? sortByStatusType2 : sortByStatus);
      case 'winrate':
        return sorted.sort(sortByWinRate);
      default:
        return sorted;
    }
  };

  // Apply sorting
  const sortedType1Bots = applySorting(type1Bots, type1SortBy);
  const sortedType2Bots = applySorting(type2Bots, type2SortBy, true);

  // Handle card click
  const handleCardClick = (botId: string) => {
    setSelectedBotId(botId);
    setIsDetailOpen(true);
  };

  // Get selected bot name
  const selectedBotName = states.find(s => s.bot_id === selectedBotId)?.bots?.name;

  return (
    <>
    <GroupActivityMonitor />
    
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* Type 1 Bots - Left Column */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              Type 1 Bots - Start/Stop After Loss
              {type1Bots.length > 0 && (
                <BulkDeleteType1Dialog totalCount={type1Bots.length} />
              )}
            </CardTitle>
            <CardDescription>
              Bots using consecutive loss streak threshold system
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Select value={type1SortBy} onValueChange={(value) => setType1SortBy(value as any)}>
              <SelectTrigger className="w-[140px] h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="name">By Name</SelectItem>
                <SelectItem value="updated">By Updated</SelectItem>
                <SelectItem value="status">By Status</SelectItem>
                <SelectItem value="winrate">By Win Rate</SelectItem>
              </SelectContent>
            </Select>
            <Button
              size="sm"
              variant="outline"
              onClick={() => refetchStates()}
              disabled={statesLoading}
            >
              <RefreshCw className={`h-4 w-4 ${statesLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {statesLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : type1Bots.length === 0 ? (
            <div className="text-center py-8 text-sm text-muted-foreground">
              No Type 1 bots configured
            </div>
          ) : (
            <div className="grid grid-cols-2 xl:grid-cols-3 gap-2">
              {sortedType1Bots.map((state) => (
                <Type1BotCard 
                  key={state.id} 
                  state={state}
                  winRate={winRateStats?.get(state.bot_id)?.win_rate}
                  totalExits={winRateStats?.get(state.bot_id)?.total_forwarded_exits}
                  onClick={() => handleCardClick(state.bot_id)}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Type 2 Bots - Right Column */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Type 2 Bots - Flexible Pattern Skip</CardTitle>
            <CardDescription>
              Bots using flexible pattern matching with recent results tracking
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Select value={type2SortBy} onValueChange={(value) => setType2SortBy(value as any)}>
              <SelectTrigger className="w-[140px] h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="name">By Name</SelectItem>
                <SelectItem value="updated">By Updated</SelectItem>
                <SelectItem value="status">By Status</SelectItem>
                <SelectItem value="winrate">By Win Rate</SelectItem>
              </SelectContent>
            </Select>
            <Button
              size="sm"
              variant="outline"
              onClick={() => refetchStates()}
              disabled={statesLoading}
            >
              <RefreshCw className={`h-4 w-4 ${statesLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {statesLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : type2Bots.length === 0 ? (
            <div className="text-center py-8 text-sm text-muted-foreground">
              No Type 2 bots configured
            </div>
          ) : (
            <div className="grid grid-cols-2 xl:grid-cols-3 gap-2">
              {sortedType2Bots.map((state) => (
                <Type2BotCard 
                  key={state.id} 
                  state={state}
                  winRate={winRateStats?.get(state.bot_id)?.win_rate}
                  totalExits={winRateStats?.get(state.bot_id)?.total_forwarded_exits}
                  activeLockPattern={state.active_lock_pattern_id 
                    ? type2LockPatterns?.get(state.active_lock_pattern_id) 
                    : null
                  }
                  hasReversePatterns={state.hasReversePatterns}
                  onClick={() => handleCardClick(state.bot_id)}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>

    {/* Win Rate Detail Dialog */}
    {selectedBotId && (
      <BotWinRateDetailDialog
        open={isDetailOpen}
        onOpenChange={setIsDetailOpen}
        botId={selectedBotId}
        botName={selectedBotName}
      />
    )}
  </>
  );
};